import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { Campaign } from "../api/types";
import { Button } from "../ui/Button";
import { CallNotesFields, callNotesFrom, sameCallNotes, type CallNotes } from "./CallNotesFields";
import { Spinner } from "./Spinner";

/* A campaign's own words on its Orum list: an angle for each stage its
 * criteria can return, the incentive line, and the closing. Asked for here,
 * before the first Orum export, rather than when the campaign is made, since
 * the export is when they are used. docs/plans/campaigns-overhaul.md, The
 * Call notes tab.
 *
 * A campaign with no closing is offered the newest closing another campaign
 * saved, unsaved until Save, because the closing is Buzze's own pitch and an
 * empty one prints nothing on the note. */
export function CampaignCallNotes({ token, c }: { token: string; c: Campaign }) {
  const name = c.name ?? "";
  const saved = callNotesFrom(c.shape);
  const queryClient = useQueryClient();
  const [said, setSaid] = useState<string | null>(null);
  const [draft, setDraft] = useState<CallNotes>(saved);
  const [offered, setOffered] = useState<string | null>(null);

  useQuery({
    queryKey: ["newest-closing", name],
    enabled: !saved.closing.trim(),
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("criteria_set")
        .select("name,label,shape").neq("name", name).not("shape->>call_note_closing", "is", null)
        .order("updated_at", { ascending: false }).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      const row = (data ?? [])[0] as { name?: string | null; label?: string | null; shape?: unknown } | undefined;
      const other = row ? callNotesFrom(row.shape) : null;
      if (other?.closing.trim()) {
        setDraft((d) => (d.closing.trim() ? d : { ...d, closing: other.closing, incentives: d.incentives || other.incentives }));
        setOffered(row?.label || row?.name || null);
      }
      return other;
    },
  });

  const save = useMutation({
    mutationFn: () => callFunction<CallNotes>(token, "set_call_notes", {
      p_name: name, p_angles: draft.angles, p_incentives: draft.incentives, p_closing: draft.closing,
    }),
    onSuccess: (kept) => {
      setDraft(kept ?? draft);
      setOffered(null);
      setSaid("Saved. The next Orum list prints these.");
      queryClient.invalidateQueries({ queryKey: ["campaign", name] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  return (
    <section className="rp-tabbody cn" aria-label="Call notes">
      <p className="muted">An angle for each stage this campaign can return. It prints on every property at that stage.</p>
      {offered ? <p className="cn-offered">The closing is from {offered}. Save to use it here.</p> : null}
      <CallNotesFields token={token} filters={c.filters} value={draft} onChange={(v) => { setDraft(v); setSaid(null); }} />
      <div className="bar">
        <Button kind="orange" onClick={() => save.mutate()} disabled={save.isPending || sameCallNotes(draft, saved)}>
          {save.isPending ? <><Spinner />Saving</> : "Save"}
        </Button>
        {said ? <span className={save.isError ? "refusal" : "muted"} role="status">{said}</span> : null}
      </div>
    </section>
  );
}
